import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import PageTitleBar from "@/components/PageTitleBar";
import ACL_RELATIONSHIPS from "acl-relationships";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import { ImportTargetProvider } from "./ImportTargetContext";
import Stepper from "./Stepper";
import UploadStep from "./steps/UploadStep";
import Step3Mapping from "./steps/Step3Mapping";
import Step4Run from "./steps/Step4Run";

const STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Mapping" },
  { id: 3, label: "Run" },
];

const ImportWizard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNewTable = searchParams.get("mode") === "new_table";
  const [currentStep, setCurrentStep] = useState(1);
  const canImport = useAuthorized([ACL_RELATIONSHIPS.customTable.import]);

  usePageTitle("Import");

  const goNext = () => setCurrentStep(s => Math.min(s + 1, STEPS.length));
  const goBack = () => setCurrentStep(s => Math.max(s - 1, 1));
  const handleCancel = () =>
    navigate(isNewTable ? "/settings/custom_tables/new" : "/data");
  // A finished import lands on the imported table's grid rather than back
  // where the wizard was opened from.
  const handleDone = slug =>
    navigate(slug ? `/custom_tables/${slug}` : "/data");

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <UploadStep onNext={goNext} showTablePicker={!isNewTable} />;
      case 2:
        return <Step3Mapping onNext={goNext} onBack={goBack} />;
      case 3:
        return (
          <Step4Run
            onBack={goBack}
            onCancel={handleCancel}
            onDone={handleDone}
          />
        );
      default:
        return null;
    }
  };

  if (!canImport) {
    return (
      <div className="tw:w-full tw:self-start tw:flex tw:flex-col tw:gap-2 tw:text-[var(--color-text-secondary)]">
        <p className="tw:m-0">
          You don&apos;t have permission to import custom table data.
        </p>
        <Link
          to="/data"
          className="tw:self-start tw:inline-flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-muted-foreground! tw:no-underline tw:hover:text-foreground!"
        >
          <ArrowLeft className="tw:size-4" />
          Back to Data
        </Link>
      </div>
    );
  }

  return (
    <ImportTargetProvider initialMode={isNewTable ? "new_table" : "append"}>
      <div className="tw:w-full tw:self-start tw:flex tw:flex-col tw:gap-4">
        <PageTitleBar
          backTo="/settings/custom_tables"
          backLabel="Back to Custom Tables"
          title="Import CSV"
          subtitle={
            isNewTable
              ? "Build a new custom table from a CSV file."
              : "Append rows to a custom table from a CSV file."
          }
        />

        <div className="tw:px-6 tw:py-4 tw:bg-[var(--color-surface)] tw:border tw:border-[var(--color-border)] tw:rounded-lg">
          <Stepper steps={STEPS} currentStep={currentStep} />
        </div>

        <main className="tw:bg-[var(--color-surface)] tw:border tw:border-[var(--color-border)] tw:rounded-lg">
          {renderStep()}
        </main>
      </div>
    </ImportTargetProvider>
  );
};

export default ImportWizard;
